import React, { useEffect } from "react";
import toast, { Toaster } from 'react-hot-toast';
import { Search, UploadCloud, Trash2, FileText, Folder, Database, X } from "lucide-react";
import { TreeItem, filterTree, getExpandedPaths, getAllSelectableItems } from "./AssetSelectionHelpers";

export const AssetSelectionSidebar = ({
  activeTab,
  availableTabs,
  setActiveTab,
  selectedFolders,
  uploadedFiles,
  adlsData,
  ucData,
  ucGcpData,
  searchTerm,
  setSearchTerm,
  isDragActive,
  handleDrag,
  handleDrop,
  handleFileChange,
  handleSelectAll,
  handleClear,
  handleItemSelect,
  loading,
  ErrorMessage,
}) => {
  const adlsCount = React.useMemo(
    () => getAllSelectableItems(adlsData, "adls", (item) => true).length,
    [adlsData]
  );
  const ucCount = React.useMemo(
    () => getAllSelectableItems(ucData, "uc", (item, source) => !item.children || item.type === "table").length,
    [ucData]
  );
  const ucGcpCount = React.useMemo(
    () => getAllSelectableItems(ucGcpData, "uc_gcp", (item, source) => !item.children || item.type === "table").length,
    [ucGcpData]
  );
  const manualCount = uploadedFiles.length;

  const expandedPaths = React.useMemo(() => {
    if (!searchTerm) return new Set();
    const currentData =
      activeTab === "adls" ? adlsData : activeTab === "uc" ? ucData : activeTab === "uc_gcp" ? ucGcpData : [];
    return getExpandedPaths(
      currentData,
      searchTerm,
      "",
      activeTab === "uc" || activeTab === "uc_gcp"
        ? (item) => !item.children || item.type === "table"
        : () => true
    );
  }, [searchTerm, adlsData, ucData, ucGcpData, activeTab]);

  useEffect(() => {
    if (!ErrorMessage) return;
    Object.values(ErrorMessage).forEach((source) => {
      if (source?.errors?.length) {
        source.errors.forEach((err) => {
          const errMsg = err?.message;
          if (errMsg) {
            toast.error(errMsg);
          }
        });
      }
    });
  }, [ErrorMessage]);

  const renderTree = () => {
    const filteredData =
      activeTab === "adls"
        ? filterTree(adlsData, searchTerm)
        : activeTab === "uc_gcp"
        ? filterTree(ucGcpData, searchTerm)
        : filterTree(ucData, searchTerm);

    const isSelectable =
      activeTab === "uc" || activeTab === "uc_gcp"
        ? (item) => !item.children || item.type === "table"
        : () => true;

    return filteredData.map((item, index) => (
      <TreeItem
        key={index}
        item={item}
        source={activeTab}
        onSelect={handleItemSelect}
        selectedItems={selectedFolders}
        searchTerm={searchTerm}
        expandedPaths={expandedPaths}
        isSelectable={isSelectable}
      />
    ));
  };

  return (
    <div className="w-1/3 border-r p-4 flex flex-col">
      <div className="flex gap-2 mb-4">
        {availableTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm rounded-md flex items-center gap-2 border ${
              activeTab === tab.key
                ? "bg-[#FF03F3] text-white border-blue-600"
                : "bg-white"
            }`}
          >
            {tab.label}
            <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {tab.key === "adls" ? adlsCount : tab.key === "uc" ? ucCount : tab.key === "uc_gcp" ? ucGcpCount : manualCount}
            </span>
          </button>
        ))}
      </div>

      {activeTab !== "manual" && (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border rounded-md"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                aria-label="Clear search"
              >
                <X className="h-5" />
              </button>
            )}
          </div>
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              Select All (
              {activeTab === "adls"
                ? adlsCount
                : activeTab === "uc"
                ? ucCount
                : activeTab === "uc_gcp"
                ? ucGcpCount
                : manualCount}
              )
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 text-sm rounded-md text-white bg-gray-600 hover:bg-gray-700"
            >
              Clear Selection
            </button>
          </div>
        </>
      )}

      <div className="flex-grow overflow-y-auto pr-2">
        {loading && (
          <div className="flex flex-col justify-center items-center h-full">
            <span className="mt-2 text-gray-700">Loading Data Assets...</span>
          </div>
        )}

        {!loading && activeTab !== "manual" && renderTree()}

        {activeTab === "manual" && !loading && (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer ${
              isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
            }`}
          >
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center w-full cursor-pointer"
            >
              <UploadCloud className="h-12 w-12 mx-auto text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Drag and drop your CSV files here, or click to browse
              </p>
              <p className="text-xs text-gray-500 mt-1">Only CSV files are accepted</p>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export const SelectedItemsPanel = ({
  selectedFolders,
  uploadedFiles,
  handleNameSave,
  onEditingNameChange,
  editingNames,
  fileConfigurations,
  openConfigModal,
  handleRemoveFolder,
  handleRemoveFile,
  allItemsConfigured,
  isSaving,
  isSubmitting,
  handleSave,
  handleSubmit,
  columnModalOpen,
}) => {
  const renderFolder = (folder) => (
    <div
      key={folder.path}
      className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 bg-[#F5FFFA] rounded-lg border w-full"

    >
      <div className="flex flex-grow min-w-0">
        {folder.source === "uc" || folder.source === "uc_gcp" ? (
          <Database className="h-5 mr-3 text-blue-500" />
        ) : (
          <Folder className="h-5 mr-3 text-yellow-500" />
        )}
        <div className="flex-grow mr-4 min-w-0">
          {folder.isEditing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editingNames[folder.path] ?? folder.assetName}
                onChange={(e) =>
                  onEditingNameChange(folder.path, e.target.value)
                }
                onBlur={() =>
                  handleNameSave(
                    folder.path,
                    editingNames[folder.path] || folder.defaultName,
                    true
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleNameSave(
                      folder.path,
                      editingNames[folder.path] || folder.defaultName,
                      true
                    );
                  }
                }}
                className="w-full p-1 border rounded"
                autoFocus
              />
              <button
                onClick={() =>
                  handleNameSave(
                    folder.path,
                    editingNames[folder.path] || folder.defaultName,
                    true
                  )
                }
                className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-start">
            <p className="font-bold break-all">{folder.name}</p>
<p className="text-[11px] text-gray-600 break-all leading-snug">
  {folder?.path?.replace(/\//g, ".")}
</p>

          </div>
          
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">

        <button
          onClick={() =>
            openConfigModal(false, folder.source, folder.path, folder.name)
          }
          className={`w-full sm:w-auto justify-center flex gap-1 items-center font-bold px-3 py-2 rounded text-sm whitespace-nowrap ${
            fileConfigurations[folder.path]
              ? "bg-green-500 text-white hover:bg-green-600"
              : "bg-[#9B00FB] text-white hover:bg-[#9B00FB]"
          }`}
        >
          <span>Configuration</span>
        </button>
        <button onClick={() => handleRemoveFolder(folder.path)}>
          <Trash2 className="h-5 text-red-500 hover:text-red-700" />
        </button>
      </div>
    </div>
  );

  const renderFile = (fileObj) => (
    <div
      key={fileObj.id}
      className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-3 bg-[#F5FFFA] rounded-lg border w-full"

    >
      <div className="flex flex-grow min-w-0">
        <FileText className="h-5 mr-3 text-gray-500" />
        <div className="flex-grow mr-4 min-w-0">
          {fileObj.isEditing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editingNames[fileObj.id] ?? fileObj.assetName}
                onChange={(e) =>
                  onEditingNameChange(fileObj.id, e.target.value)
                }
                onBlur={() =>
                  handleNameSave(
                    fileObj.id,
                    editingNames[fileObj.id] || fileObj.defaultName,
                    false
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleNameSave(
                      fileObj.id,
                      editingNames[fileObj.id] || fileObj.defaultName,
                      false
                    );
                  }
                }}
                className="w-full p-1 border rounded"
                autoFocus
              />
              <button
                onClick={() =>
                  handleNameSave(
                    fileObj.id,
                    editingNames[fileObj.id] || fileObj.defaultName,
                    false
                  )
                }
                className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          ) : (
            <div className="flex  flex-col items-start">
              <p className="font-medium break-words">{fileObj.name}</p>
<p className="text-[11px] text-gray-600 break-all">{fileObj?.path}</p>

            </div>
          )}
          <p className="text-xs text-gray-500">
            {(fileObj.size / 1024).toFixed(2)} KB · Uploaded{" "}
            {fileObj.lastModified
              ? new Date(fileObj.lastModified).toLocaleDateString()
              : ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => openConfigModal(true, "", fileObj.id, fileObj.name)}
          className={`ml-2 p-1 rounded flex gap-1 items-center font-bold p-2 rounded-md text-base ${
            fileConfigurations[fileObj.id]
              ? "bg-green-500 text-white hover:bg-green-600"
              : "bg-[#9B00FB] text-white hover:bg-[#9B00FB]"
          }`}
        >
          <span>Configuration</span>
        </button>
        <button onClick={() => handleRemoveFile(fileObj.id)}>
          <X className="h-5 text-red-500 hover:text-red-700" />
        </button>
      </div>
    </div>
  );

  const totalItems = selectedFolders.length + uploadedFiles.length;

  return (
    <div className="w-2/3 p-4 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-lg" />
        <div className="flex gap-2">
          {totalItems > 0 && (
            <button
              onClick={handleSave}
              className={`px-4 py-2 rounded-md ${
                !allItemsConfigured || isSaving || columnModalOpen
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-green-700 text-white hover:bg-green-800"
              }`}
              disabled={!allItemsConfigured || isSaving || columnModalOpen}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          )}
          {totalItems > 0 && (
            <button
              onClick={handleSubmit}
              className={`px-4 py-2 rounded-md ${
                !allItemsConfigured || isSubmitting || columnModalOpen
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#FF03F3] text-white"
              }`}
              disabled={!allItemsConfigured || isSubmitting || columnModalOpen}
            >
              {isSubmitting ? "Submitting..." : "Continue"}
            </button>
          )}
        </div>
      </div>

      <div className="flex-grow overflow-y-auto">
        {selectedFolders.length > 0 && (
          <div className="mb-6">
            <div className="space-y-2">
              {selectedFolders.map(renderFolder)}
            </div>
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">
              Uploaded Files ({uploadedFiles.length})
            </h4>
            <div className="space-y-2">
              {uploadedFiles.map(renderFile)}
            </div>
          </div>
        )}

        {totalItems === 0 && (
          <div className="text-center text-gray-500 py-10">
            <p>No items selected.</p>
            <p className="text-sm">
              Select folders, tables or upload files to see them here.
            </p>
          </div>
        )}
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

